import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Repeat, TrendingUp, TrendingDown, Trash2, Power, Calendar,
} from "lucide-react";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import EmptyState from "../components/EmptyState";
import ScopeToggle from "../components/ScopeToggle";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const FREQ_LABEL = {
  mensual: "Mensual",
  quincenal: "Quincenal",
  semanal: "Semanal",
};

const MONTH_ES_SHORT = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${MONTH_ES_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return "";
  }
}

function toDateInput(value) {
  try {
    const d = value ? new Date(value) : new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}

const EMPTY_FORM = {
  id: null,
  name: "",
  type: "gasto",
  amount: "",
  frequency: "mensual",
  next_date: toDateInput(new Date()),
  end_date: "",
  account_id: "",
  category: "",
  active: true,
};

export default function Recurrencias() {
  const navigate = useNavigate();
  const { scope } = useScope();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/recurrences", { params: { scope } }),
        api.get("/accounts", { params: { scope } }),
      ]);
      setItems(r.data);
      setAccounts(a.data);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, next_date: toDateInput(new Date()) });
    setOpen(true);
  };

  const openEdit = (rec) => {
    setForm({
      id: rec.id,
      name: rec.name,
      type: rec.type,
      amount: String(rec.amount),
      frequency: rec.frequency,
      next_date: toDateInput(rec.next_date),
      end_date: rec.end_date ? toDateInput(rec.end_date) : "",
      account_id: rec.account_id || "",
      category: rec.category || "",
      active: rec.active,
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount || !form.next_date) {
      toast.error("Completa nombre, monto y próxima fecha");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        amount: Number(form.amount),
        frequency: form.frequency,
        next_date: new Date(form.next_date + "T12:00:00Z").toISOString(),
        end_date: form.end_date ? new Date(form.end_date + "T12:00:00Z").toISOString() : null,
        account_id: form.account_id || null,
        category: form.category || "",
        active: form.active,
      };
      if (form.id) {
        await api.patch(`/recurrences/${form.id}`, payload);
        toast.success("Recurrencia actualizada");
      } else {
        await api.post("/recurrences", payload, { params: { scope } });
        toast.success("Recurrencia creada");
      }
      setOpen(false);
      window.dispatchEvent(new CustomEvent("fp:rec-changed"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rec) => {
    try {
      await api.patch(`/recurrences/${rec.id}`, { active: !rec.active });
      toast.success(rec.active ? "Recurrencia pausada" : "Recurrencia activada");
      window.dispatchEvent(new CustomEvent("fp:rec-changed"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`¿Eliminar la recurrencia "${rec.name}"?`)) return;
    try {
      await api.delete(`/recurrences/${rec.id}`);
      toast.success("Recurrencia eliminada");
      window.dispatchEvent(new CustomEvent("fp:rec-changed"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="recurrencias-screen">
      {/* Header with back */}
      <header className="pt-10 pb-4 px-6 flex items-start justify-between">
        <div className="flex-1">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="recurrencias-back"
            className="inline-flex items-center gap-1 text-soft hover:text-ink text-sm"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-sage-700">
            {scope === "shared" ? "Recurrencias compartidas" : "Recurrencias"}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
            Gastos fijos
          </h1>
          <p className="mt-1.5 text-sm text-muted2">
            {scope === "shared"
              ? "Los compromisos repetitivos del dinero común. Se usan para proyectar el saldo compartido."
              : "Lo que se repite cada mes o quincena. Se usa para proyectar tu saldo real."}
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="recurrencias-add-button"
          aria-label="Nueva recurrencia"
          className="h-10 w-10 rounded-full bg-sage-900 text-bone flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
        >
          <Plus size={18} />
        </button>
      </header>

      <div className="px-6 mt-2">
        <ScopeToggle />
      </div>

      <div className="px-6 mt-3">
        {loading ? (
          <p className="text-soft text-sm text-center py-8">Cargando…</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Repeat}
            title={scope === "shared" ? "Sin recurrencias compartidas" : "Sin recurrencias"}
            description={
              scope === "shared"
                ? "Agrega gastos fijos del espacio común (renta compartida, servicios, suscripciones de los dos) para proyectar el saldo conjunto."
                : "Agrega tu renta, suscripciones o nómina para que la proyección sea realista."
            }
            testid="recurrencias-empty"
            action={
              <button
                data-testid="recurrencias-empty-cta"
                onClick={openCreate}
                className="bg-sage-900 text-bone rounded-2xl px-5 py-3 font-bold text-sm"
              >
                Crear primera recurrencia
              </button>
            }
          />
        ) : (
          <ul className="space-y-3">
            {items.map((rec) => (
              <li
                key={rec.id}
                data-testid={`recurrence-${rec.id}`}
                className={`rounded-3xl bg-white border shadow-card p-5 ${
                  rec.active ? "border-line/70" : "border-line/50 opacity-60"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${
                    rec.type === "ingreso"
                      ? "bg-sage-50 text-sage-700"
                      : "bg-bone text-ink"
                  }`}>
                    {rec.type === "ingreso" ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-display font-bold text-lg truncate">{rec.name}</p>
                      {!rec.active && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-line/50 text-muted2 px-2 py-0.5 rounded-full">
                          Pausada
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-soft">
                      <span>{FREQ_LABEL[rec.frequency] || rec.frequency}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} /> {formatDate(rec.next_date)}
                      </span>
                    </div>
                  </div>
                  <p className={`font-display font-extrabold text-lg tabular ${
                    rec.type === "ingreso" ? "text-sage-700" : "text-ink"
                  }`}>
                    {rec.type === "ingreso" ? "+" : "−"} {formatMXN(rec.amount)}
                  </p>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(rec)}
                    data-testid={`recurrence-edit-${rec.id}`}
                    className="rounded-xl border border-line py-2 text-xs font-bold uppercase tracking-wider text-ink hover:bg-sage-50"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(rec)}
                    data-testid={`recurrence-toggle-${rec.id}`}
                    className="rounded-xl border border-line py-2 text-xs font-bold uppercase tracking-wider text-ink hover:bg-sage-50 inline-flex items-center justify-center gap-1"
                  >
                    <Power size={12} /> {rec.active ? "Pausar" : "Activar"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(rec)}
                    data-testid={`recurrence-delete-${rec.id}`}
                    className="rounded-xl border border-clay/20 text-clay py-2 text-xs font-bold uppercase tracking-wider hover:bg-clay/5 inline-flex items-center justify-center gap-1"
                  >
                    <Trash2 size={12} /> Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-bone border-line rounded-3xl" data-testid="recurrence-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {form.id ? "Editar recurrencia" : "Nueva recurrencia"}
            </DialogTitle>
            <DialogDescription className="text-soft text-sm">
              Define un compromiso fijo (gasto o ingreso) para mejorar la proyección.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* type */}
            <div className="grid grid-cols-2 gap-2 bg-white border border-line rounded-2xl p-1">
              <button
                type="button"
                data-testid="rec-type-gasto"
                onClick={() => setForm({ ...form, type: "gasto" })}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  form.type === "gasto" ? "bg-sage-900 text-bone" : "text-ink"
                }`}
              >
                <TrendingDown size={14} /> Gasto fijo
              </button>
              <button
                type="button"
                data-testid="rec-type-ingreso"
                onClick={() => setForm({ ...form, type: "ingreso" })}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  form.type === "ingreso" ? "bg-sage-900 text-bone" : "text-ink"
                }`}
              >
                <TrendingUp size={14} /> Ingreso recurrente
              </button>
            </div>

            <Input
              label="Nombre"
              testid="rec-name-input"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Ej. Renta · Spotify · Salario"
            />

            <Input
              label="Monto"
              testid="rec-amount-input"
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })}
              placeholder="0.00"
            />

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                Frecuencia
              </label>
              <div className="grid grid-cols-3 gap-2">
                {["mensual", "quincenal", "semanal"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    data-testid={`rec-freq-${f}`}
                    onClick={() => setForm({ ...form, frequency: f })}
                    className={`py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                      form.frequency === f
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-ink"
                    }`}
                  >
                    {FREQ_LABEL[f]}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Próxima fecha"
              testid="rec-date-input"
              type="date"
              value={form.next_date}
              onChange={(v) => setForm({ ...form, next_date: v })}
            />

            <Input
              label="Fecha final (opcional)"
              testid="rec-end-date-input"
              type="date"
              value={form.end_date}
              onChange={(v) => setForm({ ...form, end_date: v })}
              hint="Cuando se alcance, la recurrencia dejará de aparecer."
            />

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Cuenta (opcional)
              </label>
              <select
                data-testid="rec-account-select"
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
              >
                <option value="">Sin cuenta específica</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <Input
              label="Categoría (opcional)"
              testid="rec-category-input"
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              placeholder="Ej. Servicios"
            />

            <button
              type="submit"
              disabled={saving}
              data-testid="rec-submit-button"
              className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
            >
              {saving ? "Guardando…" : form.id ? "Guardar cambios" : "Crear recurrencia"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Input({ label, testid, type = "text", step, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
      />
      {hint && <p className="text-[11px] text-soft mt-1 leading-relaxed">{hint}</p>}
    </div>
  );
}
